import SmsCombinedPanel from "@/components/SmsCombinedPanel";
import { usePreview } from "@/contexts/PreviewContext";
/**
 * APX WorkCenter — Controller Dashboard (Gus & Amy)
 * Design: "Gold Standard" — Premium Editorial Finance
 * Client list, today's schedule, open tasks, quick links to Client Hub and Xero.
 * Stage 2: Push notifications + file transfer added.
 * Stage 3: APX Intelligence panel added.
 */

import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import SendNotificationModal from "@/components/SendNotificationModal";
import NotifyClientModal from "@/components/NotifyClientModal";
import FileDropZone from "@/components/FileDropZone";
import { trpc } from "@/lib/trpc";
import AllCommunicationsFeed from "@/components/AllCommunicationsFeed";
import ClientDocuments from "@/components/ClientDocuments";
import { ContactClientModal } from "@/components/ContactClientModal";
import XeroConnectionPanel from "@/components/XeroConnectionPanel";

// Client list is now loaded live from the database via tRPC clientStatus.listAll
// Global due date setter is available to Controller role

const TODAY_SCHEDULE = [
  { time: "9:00 AM", client: "Sunrise Café LLC", type: "Monthly Review", duration: "45 min" },
  { time: "11:00 AM", client: "Metro Plumbing Co.", type: "Catch-up Call", duration: "30 min" },
  { time: "2:30 PM", client: "Harbor View Dental", type: "Quarterly Review", duration: "60 min" },
];

const OPEN_TASKS = [
  { client: "Metro Plumbing Co.", task: "Request Jan bank statements", priority: "high" },
  { client: "Eastside Auto Repair", task: "Follow up — 2 months overdue", priority: "high" },
  { client: "Greenfield Landscaping", task: "Send Feb P&L report", priority: "medium" },
  { client: "Oakwood Retail Inc.", task: "Review client contact info", priority: "low" },
];

// ALERT_TYPES is now fully live — counts come from tRPC queries below

// ── Bookkeeper roster (mirrors BookkeepingManagerDashboard) ──────────────────
const CTRL_BOOKKEEPERS = [
  { name: "Jany L.",    email: "lopez.jany@gmail.com" },
  { name: "Irit P.",    email: "iritna@gmail.com" },
  { name: "Yunuen A.",  email: "yunuenalfaro7@gmail.com" },
  { name: "Shobhit A.", email: "shobhit@advancefinserv.com" },
  { name: "Lulu M.",    email: "lourdesmastache20@gmail.com" },
  { name: "Marian H.",  email: "marianjoyhernandez2@gmail.com" },
  { name: "Renata L.",  email: "erenatalazo@gmail.com" },
  { name: "Arvind K.",  email: "arvind@advancefinserv.com" },
  { name: "Gus G.",     email: "gus@bbnfi.com" },
];
const CTRL_T1_T3_BOOKKEEPERS = [
  ...CTRL_BOOKKEEPERS.filter(b => b.name !== "Jany L."),
  { name: "Jany L.", email: "lopez.jany@gmail.com" },
];
const CTRL_T4_BOOKKEEPERS = CTRL_BOOKKEEPERS.filter(
  b => b.name === "Jany L." || b.name === "Irit P." || b.name === "Gus G."
);
const CTRL_TIER_META: Record<string, { label: string; code: string; color: string }> = {
  T1: { label: "B/Feed",  code: "B",   color: "oklch(0.55 0.16 85)" },
  T2: { label: "Code",    code: "C",   color: "oklch(0.45 0.18 240)" },
  T3: { label: "Ano|T/B", code: "ATB", color: "oklch(0.45 0.20 300)" },
  "T3-C": { label: "C-P&L",  code: "PLB", color: "oklch(0.40 0.15 145)" },
};
const CTRL_BK_COLORS = [
  "oklch(0.82 0.01 240)",
  "oklch(0.55 0.01 240)", "oklch(0.55 0.01 240)", "oklch(0.55 0.01 240)",
  "oklch(0.55 0.01 240)", "oklch(0.55 0.01 240)", "oklch(0.55 0.01 240)",
];

const STATUS_COLORS: Record<string, string> = {
  Current: "oklch(0.55 0.01 240)",
  Review: "oklch(0.55 0.01 240)",
  Alert: "oklch(0.55 0.01 240)",
};

const PRIORITY_COLORS: Record<string, string> = {
  high: "oklch(0.55 0.01 240)",
  medium: "oklch(0.55 0.01 240)",
  low: "oklch(0.55 0.01 240)",
};

const QUICK_LINKS = [
  { label: "Xero", desc: "Financial data & reporting", url: "https://go.xero.com", icon: "💼" },
];



// ─── Assign Tax Task Panel ────────────────────────────────────────────────────────────────────
// ─── Global Due Date Setter Component ──────────────────────────────────────────
function GlobalDueDateSetter({ callerEmail }: { callerEmail: string }) {
  const [globalDueDate, setGlobalDueDate] = useState("");
  const [tierDates, setTierDates] = useState<Record<string, string>>({ T1: "", T2: "", T3: "" });
  const setGlobalDueDateMutation = trpc.tierTasks.setGlobalDueDate.useMutation({
    onSuccess: () => toast.success("Due date applied"),
    onError: (err) => toast.error("Failed to set due date", { description: err.message }),
  });
  const TIERS = [
    { key: "T1", label: "T1 — B/Feed" },
    { key: "T2", label: "T2 — Code" },
    { key: "T3", label: "T3 — ATB" },
  ];
  return (
    <div className="flex flex-col gap-2">
      {/* Set All Due */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-sm text-[oklch(0.35_0.03_240)] uppercase tracking-wider whitespace-nowrap">Set All Due:</span>
        <input
          type="date"
          value={globalDueDate}
          onChange={e => setGlobalDueDate(e.target.value)}
          className="px-2 py-1.5 rounded-lg text-sm outline-none"
          style={{ background: "oklch(0.93 0.008 240)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.12 0.04 240)", colorScheme: "light" }}
        />
        <button
          onClick={() => { if (!globalDueDate) return; const ts = new Date(globalDueDate + "T00:00:00").getTime(); setGlobalDueDateMutation.mutate({ dueAt: ts, callerEmail }); }}
          disabled={!globalDueDate || setGlobalDueDateMutation.isPending}
          className="px-3 py-1.5 rounded-lg text-sm font-semibold transition-all"
          style={{ background: "oklch(0.92 0.005 240 / 15%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)", opacity: (!globalDueDate || setGlobalDueDateMutation.isPending) ? 0.5 : 1 }}>
          {setGlobalDueDateMutation.isPending ? "Applying…" : "Apply"}
        </button>
        {globalDueDate && (
          <button
            onClick={() => { setGlobalDueDateMutation.mutate({ dueAt: null, callerEmail }); setGlobalDueDate(""); }}
            className="px-2 py-1.5 rounded-lg text-sm transition-all"
            style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
            Clear
          </button>
        )}
      </div>
      {/* Per-tier due dates */}
      <div className="flex items-center gap-2 flex-wrap">
        {TIERS.map(({ key, label }) => (
          <div key={key} className="flex items-center gap-1">
            <span className="text-xs text-[oklch(0.4_0.03_240)] uppercase tracking-wider whitespace-nowrap">{label}:</span>
            <input
              type="date"
              value={tierDates[key]}
              onChange={e => setTierDates(prev => ({ ...prev, [key]: e.target.value }))}
              className="px-1.5 py-1 rounded text-xs outline-none"
              style={{ background: "oklch(0.93 0.008 240)", border: "1.5px solid oklch(0.3 0.04 240)", color: "oklch(0.12 0.04 240)", colorScheme: "light" }}
            />
            <button
              onClick={() => {
                if (!tierDates[key]) return;
                const ts = new Date(tierDates[key] + "T00:00:00").getTime();
                setGlobalDueDateMutation.mutate({ dueAt: ts, tier: key, callerEmail });
              }}
              disabled={!tierDates[key] || setGlobalDueDateMutation.isPending}
              className="px-2 py-1 rounded text-xs font-semibold transition-all"
              style={{ background: "oklch(0.88 0.01 240 / 20%)", border: "1.5px solid oklch(0.3 0.04 240)", color: "oklch(0.1 0.02 240)", opacity: !tierDates[key] ? 0.5 : 1 }}>
              Set
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ControllerClientOnboarding({ callerEmail, onClose }: { callerEmail: string; onClose: () => void }) {
  const utils = trpc.useUtils();
  const [name, setName] = useState("");
  const [practiceKey, setPracticeKey] = useState<"apx" | "apx-hrb" | "apx-bbn">("apx-hrb");
  const [accountingPlatform, setAccountingPlatform] = useState<"xero" | "quickbooks" | "other">("xero");
  const [contactPerson, setContactPerson] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientState, setClientState] = useState("CA");
  const [industry, setIndustry] = useState("");

  const addClient = trpc.xeroPm.addCoreClient.useMutation({
    onSuccess: (data) => {
      utils.clientStatus.listAll.invalidate();
      utils.tierTasks.list.invalidate();
      toast.success(`${data.client?.name ?? "Client"} added to the operational client list`);
      onClose();
    },
    onError: (error) => toast.error("Unable to add client", { description: error.message }),
  });

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      toast.error("Company name is required");
      return;
    }
    addClient.mutate({
      callerEmail,
      name: name.trim().toUpperCase(),
      coreName: name.trim().toUpperCase(),
      practiceKey,
      isActive: true,
      accountingPlatform,
      contactPerson: contactPerson.trim() || undefined,
      clientEmail: clientEmail.trim() || undefined,
      clientPhone: clientPhone.trim() || undefined,
      clientState: clientState.trim() || undefined,
      industry: industry.trim() || undefined,
    });
  };

  const fieldClass = "w-full rounded-lg border border-[oklch(0.80_0.02_240)] bg-[oklch(0.98_0.005_240)] px-3 py-2 text-sm text-[oklch(0.12_0.04_240)] outline-none focus:border-[oklch(0.68_0.14_75)]";
  const labelClass = "mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[oklch(0.38_0.04_240)]";

  return (
    <section className="apx-card rounded-xl p-5" aria-label="Client onboarding">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-[oklch(0.12_0.04_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>Client Onboarding</h2>
          <p className="mt-1 text-sm text-[oklch(0.35_0.03_240)]">Create an operational client. The client will be available in Contacts and Schedule &amp; Task Assignment for tier and bookkeeper setup.</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg border border-[oklch(0.75_0.02_240)] px-3 py-2 text-sm font-semibold text-[oklch(0.35_0.03_240)]">Cancel</button>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className={labelClass}>Company Name *</label>
          <input className={fieldClass} value={name} onChange={event => setName(event.target.value)} placeholder="e.g. DUTCH CLEANING & MAINTENANCE SERVICES INC" autoFocus />
        </div>
        <div>
          <label className={labelClass}>Practice Entity *</label>
          <select className={fieldClass} value={practiceKey} onChange={event => setPracticeKey(event.target.value as "apx" | "apx-hrb" | "apx-bbn")}>
            <option value="apx">APX Advisors LLC</option>
            <option value="apx-hrb">APX-HRB</option>
            <option value="apx-bbn">APX-BBN</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Accounting Platform *</label>
          <select className={fieldClass} value={accountingPlatform} onChange={event => setAccountingPlatform(event.target.value as "xero" | "quickbooks" | "other")}>
            <option value="xero">Xero</option>
            <option value="quickbooks">QuickBooks</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Contact Person</label>
          <input className={fieldClass} value={contactPerson} onChange={event => setContactPerson(event.target.value)} placeholder="e.g. John Smith" />
        </div>
        <div>
          <label className={labelClass}>Email</label>
          <input className={fieldClass} type="email" value={clientEmail} onChange={event => setClientEmail(event.target.value)} placeholder="client@example.com" />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input className={fieldClass} type="tel" value={clientPhone} onChange={event => setClientPhone(event.target.value)} placeholder="(310) 555-1234" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>State</label>
            <input className={fieldClass} value={clientState} onChange={event => setClientState(event.target.value.toUpperCase())} maxLength={2} placeholder="CA" />
          </div>
          <div>
            <label className={labelClass}>Industry</label>
            <input className={fieldClass} value={industry} onChange={event => setIndustry(event.target.value)} placeholder="Restaurant" />
          </div>
        </div>
        <div className="md:col-span-2 flex items-center justify-between gap-3 border-t border-[oklch(0.88_0.02_240)] pt-4">
          <span className="text-xs text-[oklch(0.40_0.03_240)]">Controllers can create operational clients; team access remains Administrator-only.</span>
          <button type="submit" disabled={addClient.isPending} className="rounded-lg bg-[oklch(0.72_0.14_75)] px-4 py-2 text-sm font-bold text-[oklch(0.12_0.04_240)] disabled:opacity-60">{addClient.isPending ? "Adding…" : "Add Operational Client"}</button>
        </div>
      </form>
    </section>
  );
}

function AssignTaxTaskPanel({ assignedBy, assignedByEmail }: { assignedBy: string; assignedByEmail: string }) {
  const utils = trpc.useUtils();
  const [clientName, setClientName] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");

  const { data: existingTasks } = trpc.taxTasks.list.useQuery(undefined, { refetchInterval: 10000 });

  const createTask = trpc.taxTasks.create.useMutation({
    onSuccess: () => {
      toast.success("Task assigned to Jasmin", { description: "She has been notified." });
      setClientName("");
      setTitle("");
      setNotes("");
      setDueDate("");
      utils.taxTasks.list.invalidate();
    },
    onError: (err) => toast.error("Failed to assign task", { description: err.message }),
  });

  const deleteTask = trpc.taxTasks.delete.useMutation({
    onSuccess: () => utils.taxTasks.list.invalidate(),
    onError: (err) => toast.error("Failed to delete task", { description: err.message }),
  });

  const s = {
    label: { color: "oklch(0.22 0.04 240)", fontSize: "0.875rem", textTransform: "uppercase" as const, letterSpacing: "0.06em", fontWeight: 600, marginBottom: "0.4rem", display: "block" },
    input: { background: "oklch(0.95 0.006 240)", border: "2px solid oklch(0.25 0.04 240)", borderRadius: "0.5rem", padding: "0.5rem 0.75rem", color: "oklch(0.12 0.04 240)", fontSize: "0.9rem", width: "100%", outline: "none" },
  };

  const STATUS_LABELS: Record<string, string> = {
    not_started: "Not Started",
    in_progress: "In Progress",
    complete: "Complete",
  };
  const STATUS_COLORS_MAP: Record<string, string> = {
    not_started: "oklch(0.55 0.04 240)",
    in_progress: "oklch(0.82 0.01 240)",
    complete: "oklch(0.82 0.01 240)",
  };

  return (
    <div className="space-y-4">
      {/* Assign form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!clientName || !title) return;
          createTask.mutate({ assignedBy, assignedByEmail, clientName, title, notes: notes || undefined, dueDate: dueDate || undefined });
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label style={s.label}>Client Name</label>
            <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="e.g. Sunrise Café" required style={s.input} />
          </div>
          <div>
            <label style={s.label}>Due Date (optional)</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={s.input} />
          </div>
        </div>
        <div>
          <label style={s.label}>Task Description</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Prepare 2025 tax return" required style={s.input} />
        </div>
        <div>
          <label style={s.label}>Notes (optional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any context or specific instructions…" rows={2} style={{ ...s.input, resize: "none" }} />
        </div>
        <button
          type="submit"
          disabled={createTask.isPending}
          className="w-full py-2 rounded-lg text-sm font-bold transition-all"
          style={{ background: "linear-gradient(135deg, oklch(0.82 0.01 240), oklch(0.55 0.01 240))", color: "oklch(0.12 0.04 240)", opacity: createTask.isPending ? 0.7 : 1 }}
        >
          {createTask.isPending ? "Assigning…" : "Assign Task to Jasmin"}
        </button>
      </form>

      {/* Existing tasks */}
      {existingTasks && existingTasks.length > 0 && (
        <div className="space-y-1.5">
          <p style={{ color: "oklch(0.22 0.04 240)", fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Active Tasks</p>
          {existingTasks.slice(0, 8).map((task) => (
            <div key={task.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg" style={{ background: "oklch(0.95 0.006 240)" }}>
              <div className="min-w-0 flex-1">
                <p style={{ color: "oklch(0.12 0.04 240)", fontSize: "0.875rem", fontWeight: 600 }} className="truncate">{task.title}</p>
                <p style={{ color: "oklch(0.22 0.04 240)", fontSize: "0.68rem" }}>{task.clientName}</p>
              </div>
              <span style={{ color: "oklch(0.08 0.01 240)", fontSize: "0.68rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                {STATUS_LABELS[task.status] ?? task.status}
              </span>
              <button
                onClick={() => deleteTask.mutate({ id: task.id })}
                className="shrink-0 text-sm px-1.5 py-0.5 rounded"
                style={{ color: "oklch(0.08 0.01 240)", background: "oklch(0.92 0.005 240 / 10%)" }}
                title="Remove task"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_DISPLAY: Record<string, { label: string; color: string }> = {
  not_started:      { label: "Not Started", color: "oklch(0.45 0.02 240)" },
  in_progress_T1:   { label: "In Progress", color: "oklch(0.55 0.16 85)" },   // T1 Bank Feed — yellow
  in_progress_T2:   { label: "In Progress", color: "oklch(0.45 0.18 240)" },  // T2 Code — blue
  in_progress_T3:   { label: "In Progress", color: "oklch(0.45 0.20 300)" },  // T3 Anomaly/T&B — purple
  "in_progress_T3-C": { label: "In Progress", color: "oklch(0.40 0.15 145)" },  // T3-C P&L Done — green
  in_progress:      { label: "In Progress", color: "oklch(0.55 0.16 85)" },   // fallback yellow
  complete:         { label: "Complete",    color: "oklch(0.40 0.15 145)" },  // green
  overdue:          { label: "Overdue",     color: "oklch(0.50 0.20 25)" },   // red-orange
};

// ─── Sent Messages Panel ──────────────────────────────────────────────────────
const MSG_TYPE_LABELS: Record<string, string> = {
  status_update: "Status Update",
  document_request: "Document Request",
  pl_ready: "P&L for Review",
  month_end_close: "Month-End Close",
  custom: "Custom",
};


export default function ControllerDashboard() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const { setPreviewRole } = usePreview();
  const [showNotify, setShowNotify] = useState(false);
  const [showNotifyClient, setShowNotifyClient] = useState(false);
  const [notifyClientDefaults, setNotifyClientDefaults] = useState<{ clientName?: string }>({});
  const [contactModal, setContactModal] = useState<{ clientName: string; clientEmail?: string | null; clientPhone?: string | null } | null>(null);
  const [expandedDrive, setExpandedDrive] = useState<string | null>(null);
  const [expandedBkCard, setExpandedBkCard] = useState<string | null>(null);
  const [editingContact, setEditingContact] = useState<string | null>(null); // clientName being inline-edited
  const [editContactEmail, setEditContactEmail] = useState("");
  const [editContactPhone, setEditContactPhone] = useState("");
  const [editContactNotes, setEditContactNotes] = useState("");
  const [showFiles, setShowFiles] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showClientOnboarding, setShowClientOnboarding] = useState(
    () => new URLSearchParams(window.location.search).get("onboard") === "client"
  );

  const utils = trpc.useUtils();

  // Reset Monthly Matrix — Controller-level action
  const resetMonthlyMutation = trpc.tierTasks.resetMonthly.useMutation({
    onSuccess: (result) => {
      utils.tierTasks.list.invalidate();
      utils.clientStatus.listAll.invalidate();
      setShowResetConfirm(false);
      toast.success(`Monthly matrix reset complete — ${result.resetCount} tasks reset (${result.skippedAnnual} annual clients skipped)`);
    },
    onError: (err) => {
      setShowResetConfirm(false);
      toast.error("Reset failed", { description: err.message });
    },
  });

  // Inline contact edit mutation
  const updateContactMutation = trpc.clientContact.update.useMutation({
    onSuccess: () => {
      utils.clientStatus.listAll.invalidate();
      toast.success("Contact info saved");
      setEditingContact(null);
    },
    onError: (err) => toast.error("Failed to save contact", { description: err.message }),
  });

  const setWorkCycleOverride = trpc.clientStatus.setWorkCycleOverride.useMutation({
    onSuccess: (_result, variables) => {
      utils.clientStatus.listAll.invalidate();
      utils.tierTasks.list.invalidate();
      utils.tierTasks.reconciliation.invalidate();
      utils.tierTasks.t4Stats.invalidate();
      toast.success(variables.active ? "Client work cycle activated" : "Client returned to scheduled cycle");
    },
    onError: (err) => toast.error("Unable to change client work cycle", { description: err.message }),
  });

  // P&L for Review count — from shared docs folder
  const { data: plReadyFiles } = trpc.docs.plReadyCount.useQuery(undefined, { refetchInterval: 60000 });
  // T3-C completion stats — wires Month-End Progress gauge to actual tier completions
  const { data: t4Stats } = trpc.tierTasks.t4Stats.useQuery(undefined, { refetchInterval: 5000 });

  // All tier tasks — used to derive per-client status (same logic as BKKPR Manager)
  const { data: allTierTasks = [] } = trpc.tierTasks.list.useQuery({}, { refetchInterval: 5000 });

  // Derive per-client status from tier tasks — tracks active tier for color-coded badges
  // T1=yellow, T2=blue, T3=purple, T3 complete=C-P&L (green)
  // CANONICAL RULE: T3 status='complete' IS C-P&L. No separate T3-C task.
  const clientStatusFromTiers = useMemo(() => {
    const map: Record<number, string> = {};
    const byClient: Record<number, typeof allTierTasks> = {};
    for (const t of allTierTasks) {
      if (!byClient[t.clientStatusId]) byClient[t.clientStatusId] = [];
      byClient[t.clientStatusId].push(t);
    }
    for (const [id, tasks] of Object.entries(byClient)) {
      // C-P&L = T3 task status is 'complete'
      const t3Complete = tasks.some(t => t.tier === "T3" && t.status === "complete");
      if (t3Complete) { map[Number(id)] = "complete"; continue; }
      if (tasks.some(t => t.status === "overdue")) { map[Number(id)] = "overdue"; continue; }
      if (tasks.some(t => t.status === "in_progress" || t.status === "complete")) {
        // Find the furthest active tier to determine badge color
        const tierOrder = ["T1", "T2", "T3"] as const;
        let activeTier: string = "T1";
        for (const tier of tierOrder) {
          const task = tasks.find(t => t.tier === tier);
          if (task && (task.status === "in_progress" || task.status === "complete")) {
            activeTier = tier;
          }
        }
        map[Number(id)] = `in_progress_${activeTier}`;
        continue;
      }
      map[Number(id)] = "not_started";
    }
    return map;
  }, [allTierTasks]);

  // Live client roster from database — 5s polling to stay in sync with bookkeeper updates
  const [clientDataUpdatedAt, setClientDataUpdatedAt] = useState<Date | null>(null);
  const { data: allClients, isLoading: clientsLoading, dataUpdatedAt: clientsDataUpdatedAt } = trpc.clientStatus.listAll.useQuery(
    undefined,
    { refetchInterval: 5000 }
  );
  // Track when client data was last refreshed from the server
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => { if (clientsDataUpdatedAt) setClientDataUpdatedAt(new Date(clientsDataUpdatedAt)); }, [clientsDataUpdatedAt]);
  // SOP: Live bookkeeper list from DB — auto-includes new bookkeepers added via Team & Access
  const { data: liveBookkeepers = [] } = trpc.team.listBookkeepers.useQuery(undefined, { refetchInterval: 60_000, staleTime: 30_000 });

  const filteredClients = useMemo(() => {
    if (!allClients) return [];
    return allClients.filter((c) => {
      const matchesSearch = c.clientName.toLowerCase().includes(clientSearch.toLowerCase()) ||
        c.bookkeeperBankFeed.toLowerCase().includes(clientSearch.toLowerCase()) ||
        c.bookkeeperCoding.toLowerCase().includes(clientSearch.toLowerCase());
      const derivedStatus = clientStatusFromTiers[c.id] ?? "not_started";
      // When filtering by "in_progress", match all tier-specific in_progress keys
      const matchesStatus = statusFilter === "all" ||
        (statusFilter === "in_progress" && derivedStatus.startsWith("in_progress")) ||
        derivedStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [allClients, clientSearch, statusFilter, clientStatusFromTiers]);

  const overdueCount = useMemo(() => Object.values(clientStatusFromTiers).filter(s => s === "overdue").length, [clientStatusFromTiers]);

  // Live anomaly count from analytics_anomalies table
  const { data: openAnomalies } = trpc.analytics.getAllOpenAnomalies.useQuery(
    { period: new Date().toISOString().slice(0, 7) },
    { refetchInterval: 60000 }
  );
  const anomalyCount = openAnomalies?.length ?? null;

  // Not-started count = monthly clients not yet begun (tier-derived)
  const missingDataCount = allClients
    ? allClients.filter(c => !c.isAnnual && (clientStatusFromTiers[c.id] ?? "not_started") === "not_started").length
    : null;

  // Overdue close = clients with tier-derived overdue status
  const overdueCloseCount = overdueCount;

  // Xero active count for reconciliation anchor
  const { data: xeroActiveData } = trpc.xero.activeCount.useQuery(undefined, { staleTime: 60_000 });
  const xeroTotalCount = xeroActiveData?.count ?? null;

  // Tier Reconciliation — 5 columns: NYS | T1 | T2 | T3 | T3(C)
  // Each client is in exactly ONE stage. Priority: T3(C) > T3 > T2 > T1 > NYS
  // NYS = no tasks assigned. T3(C) = T3 task complete (financials ready).
  const tierReconCounts = useMemo(() => {
    // SOP: scorecard counts MONTHLY clients + bi-annual clients in active months (Jul/Dec/Jan)
    // Score each client at their HIGHEST TIER PRESENT (T3-C > T3 > T2 > T1)
    // The tier column determines the bucket — status is tracked within each tier
    // NYS = client's highest tier is T1 AND T1 status is 'not_started'
    const _reconCurrentMonth = new Date().getMonth();
    const _reconBiAnnualActive = [0, 6, 11].includes(_reconCurrentMonth);
    const reportableClientIds = new Set(
      (allClients ?? []).filter(c => !c.isAnnual || _reconBiAnnualActive).map(c => c.id)
    );
    // Only count clients with a T1 assignment (matching server getTierReconciliation filter)
    const assignedClientIds = new Set(
      allTierTasks
        .filter(t => t.tier === "T1" && t.assigneeName && t.assigneeName !== "")
        .map(t => t.clientStatusId)
    );
    const tasksByClient = new Map<number, typeof allTierTasks>();
    for (const task of allTierTasks) {
      if (!reportableClientIds.has(task.clientStatusId)) continue; // skip inactive bi-annual clients
      if (!assignedClientIds.has(task.clientStatusId)) continue; // skip clients with no T1 assignment
      if (!tasksByClient.has(task.clientStatusId)) tasksByClient.set(task.clientStatusId, []);
      tasksByClient.get(task.clientStatusId)!.push(task);
    }

    let nys = 0, t1 = 0, t2 = 0, t3 = 0, t3c = 0;

    for (const [, tasks] of tasksByClient) {
      // SOP: highest tier where a selection has been made (any status != not_started)
      // T3-C = Complete only. T1/T2/T3 = any selection made. NYS = no selection made yet.
      // SOP: T3-C IS T3 complete. Selecting "Complete" on T3 = T3-C.
      const t3Task = tasks.find(t => t.tier === "T3");
      const isT3C  = t3Task?.status === "complete";
      const isT3   = t3Task?.status === "in_progress" || t3Task?.status === "overdue";
      const hasT2  = tasks.some(t => t.tier === "T2" && t.status !== "not_started");
      const hasT1  = tasks.some(t => t.tier === "T1" && t.status !== "not_started");

      if (isT3C)        t3c++;
      else if (isT3)    t3++;
      else if (hasT2)   t2++;
      else if (hasT1)   t1++;
      else              nys++; // nothing touched yet — default starting point
    }

    return { nys, t1, t2, t3, t3c };
  }, [allTierTasks]);

  // Month-End Progress stats (Annual Review clients excluded) — tier-derived
  const total = allClients?.length ?? 0;
  const annualCount = allClients?.filter(c => c.isAnnual).length ?? 0;
  // Bi-annual clients are active (reportable) in July (6), December (11), and January (0)
  const _ctrlCurrentMonth = new Date().getMonth();
  const _ctrlBiAnnualActive = [0, 6, 11].includes(_ctrlCurrentMonth);
  const monthlyClients = allClients?.filter(c => !c.isAnnual || _ctrlBiAnnualActive) ?? [];
  const complete = monthlyClients.filter(c => (clientStatusFromTiers[c.id] ?? "not_started") === "complete").length;
  const monthlyTotal = monthlyClients.length;
  const pct = monthlyTotal > 0 ? Math.round((complete / monthlyTotal) * 100) : 0;
  // Monthly-only C-P&L complete count (excludes annual clients) — for progress bar sub-text
  const monthlyClientIdSet = useMemo(() => new Set(monthlyClients.map(c => c.id)), [monthlyClients]);
  const monthlyT3cCount = useMemo(() =>
    new Set(
      allTierTasks
        .filter((t) => monthlyClientIdSet.has(t.clientStatusId) && t.tier === "T3" && t.status === "complete")
        .map((t) => t.clientStatusId)
    ).size
  , [allTierTasks, monthlyClientIdSet]);
  // ── Schedule & Task Summary — tier-based workload (replaces old Team Workload) ──
  // SOP: use live DB bookkeepers for email list; fall back to hardcoded if not loaded yet
  const ctrlBookkeeperEmails = liveBookkeepers.length > 0
    ? liveBookkeepers.map(b => b.email)
    : CTRL_T1_T3_BOOKKEEPERS.map(b => b.email);
  const { data: ctrlTaskNotifCounts = {} } = trpc.taskNotifications.countByEmails.useQuery(
    { emails: ctrlBookkeeperEmails },
    { refetchInterval: 5000 }
  );
  const annualClientIds = useMemo(() => new Set((allClients ?? []).filter(c => c.isAnnual).map(c => c.id)), [allClients]);
  const workload = useMemo(() => {
    // Count each client ONCE at their highest tier, attributed to the bookkeeper
    // who owns that highest-tier task. Tier priority: T3-C > T3 > T2 > T1.
    // T3-C = P&L Done tier (formerly T4).
    // T3-C = T3 task with status='complete'. Priority: T3-C(4) > T3(3) > T2(2) > T1(1)
    const TIER_PRIORITY: Record<string, number> = { T3: 3, T2: 2, T1: 1 };
    const effectivePriority = (t: typeof allTierTasks[0]): number => {
      if (t.tier === "T3" && t.status === "complete") return 4; // T3-C
      return TIER_PRIORITY[t.tier] ?? 0;
    };
    const clientHighestTier = new Map<number, typeof allTierTasks[0]>();
    for (const t of allTierTasks) {
      if (annualClientIds.has(t.clientStatusId)) continue;
      const existing = clientHighestTier.get(t.clientStatusId);
      if (!existing || effectivePriority(t) > effectivePriority(existing)) {
        clientHighestTier.set(t.clientStatusId, t);
      }
    }

    // SOP: use live DB bookkeepers; fall back to hardcoded list if DB hasn't loaded yet
    const allStaff = liveBookkeepers.length > 0
      ? liveBookkeepers
      : [...CTRL_T1_T3_BOOKKEEPERS, ...CTRL_T4_BOOKKEEPERS].filter(
          (bk, idx, arr) => arr.findIndex(b => b.email === bk.email) === idx
        );

    return allStaff.map((bk, i) => {
      const myClients: typeof allTierTasks[0][] = [];
      clientHighestTier.forEach((task) => {
        if (task.assigneeEmail === bk.email) myClients.push(task);
      });

      const t1 = myClients.filter(t => t.tier === "T1").length;
      const t2 = myClients.filter(t => t.tier === "T2").length;
      const t3 = myClients.filter(t => t.tier === "T3" && t.status !== "complete").length;
      const t3c = myClients.filter(t => t.tier === "T3" && t.status === "complete").length; // T3-C = T3 complete
      const done = myClients.filter(t => t.status === "complete").length;
      const od = myClients.filter(t => t.status === "overdue").length;
      // Sort clients: T3-C first (T3+complete), then T3, T2, T1; within tier sort by name
      const effectiveTierOrder = (t: typeof allTierTasks[0]) =>
        t.tier === "T3" && t.status === "complete" ? 0 : t.tier === "T3" ? 1 : t.tier === "T2" ? 2 : 3;
      const sortedClients = [...myClients].sort((a, b) => {
        const tierDiff = effectiveTierOrder(a) - effectiveTierOrder(b);
        if (tierDiff !== 0) return tierDiff;
        return (a.clientName ?? "").localeCompare(b.clientName ?? "");
      });
      return {
        name: bk.name, email: bk.email,
        t1, t2, t3, t3c,
        total: myClients.length, complete: done, overdue: od,
        color: CTRL_BK_COLORS[i % CTRL_BK_COLORS.length],
        initials: bk.name.slice(0, 2).toUpperCase(),
        clients: sortedClients,
      };
    });
  }, [allTierTasks, annualClientIds, liveBookkeepers]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 animate-fade-slide-up">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-2xl font-bold text-[oklch(0.12_0.04_240)]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {getGreeting()}, {user?.name}
            </h1>
          </div>
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 shrink-0 items-center">
            {["controller", "asst_controller"].includes(user?.role ?? "") && (
              <button
                onClick={() => setShowClientOnboarding(value => !value)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
                style={{ background: "oklch(0.72 0.14 75)", border: "2px solid oklch(0.62 0.12 75)", color: "oklch(0.12 0.04 240)" }}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                {showClientOnboarding ? "Close Onboarding" : "Add Client"}
              </button>
            )}
            {/* Global due date setter — Controller only, not Asst Controller */}
            {["controller", "asst_controller", "bookkeeping_manager", "bookkeeper_supervisor"].includes(user?.role ?? "") && (
              <GlobalDueDateSetter callerEmail={user?.email ?? ""} />
            )}
            {/* Notify Client */}
            <button
              onClick={() => { setNotifyClientDefaults({}); setShowNotifyClient(true); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{
                background: "oklch(0.92 0.005 240 / 12%)",
                border: "2px solid oklch(0.25 0.04 240)",
                color: "oklch(0.08 0.01 240)",
              }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Notify Client
            </button>
            {/* Notify Team */}
            <button
              onClick={() => setShowNotify(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{
                background: "oklch(0.92 0.005 240 / 12%)",
                border: "2px solid oklch(0.25 0.04 240)",
                color: "oklch(0.08 0.01 240)",
              }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Notify Team
            </button>
            <button
              onClick={() => setShowFiles(!showFiles)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{
                background: "oklch(0.92 0.005 240 / 12%)",
                border: "2px solid oklch(0.25 0.04 240)",
                color: "oklch(0.08 0.01 240)",
              }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {showFiles ? "Hide Files" : "Send Files"}
            </button>
            {/* Reset Month — Controller & Director only */}
            <button
              onClick={() => setShowResetConfirm(true)}
              disabled={resetMonthlyMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.55 0.18 25 / 12%)", border: "2px solid oklch(0.55 0.18 25 / 50%)", color: "oklch(0.40 0.18 25)", opacity: resetMonthlyMutation.isPending ? 0.6 : 1 }}
              title="SOP: Reset all bookkeeping progress to Not Started for the new month"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v5h5" />
              </svg>
              {resetMonthlyMutation.isPending ? "Resetting…" : "Reset Month"}
            </button>
            {/* Preview as Bkkpr Mgr */}
            <button
              onClick={() => setPreviewRole("bookkeeping_manager")}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{
                background: "oklch(0.65 0.18 142 / 10%)",
                border: "2px solid oklch(0.65 0.18 142 / 40%)",
                color: "oklch(0.40 0.18 142)",
              }}
              title="Preview the Bookkeeping Manager dashboard view"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Bkkpr Mgr
            </button>
          </div>
        </div>

        {showClientOnboarding && (
          <ControllerClientOnboarding callerEmail={user?.email ?? ""} onClose={() => {
            setShowClientOnboarding(false);
            window.history.replaceState(null, "", "/dashboard");
          }} />
        )}

        {/* File transfer panel */}
        {showFiles && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="apx-card rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[oklch(0.08_0.01_240)] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Send Files
              </h3>
              <FileDropZone />
            </div>
          </div>
        )}

        {/* ── Portfolio KPI Bar (replaces Month-End Progress gauge) ── */}
        <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
          {[
            { label: "Total Clients", value: String(total),                                    sub: "T1-assigned portfolio", color: "oklch(0.08 0.01 240)" },
            { label: "Annual Review", value: String(annualCount),                              sub: "Bi-annual clients",     color: "oklch(0.35 0.08 270)" },
            { label: "Complete",      value: String(tierReconCounts.t3c),                      sub: "C-P&L done",           color: "oklch(0.08 0.01 240)" },
            { label: "In Progress",   value: String(tierReconCounts.t1 + tierReconCounts.t2 + tierReconCounts.t3), sub: "T1/T2/T3 active", color: "oklch(0.08 0.01 240)" },
            { label: "Not Started",   value: String(tierReconCounts.nys),                      sub: "NYS this month",       color: "oklch(0.22 0.04 240)" },
          ].map(card => (
            <div key={card.label} className="apx-card rounded-xl p-4">
              <p className="text-sm text-[oklch(0.35_0.03_240)] uppercase tracking-wider mb-1">{card.label}</p>
              <p className="text-2xl font-bold mb-0.5" style={{ fontFamily: "'Playfair Display', serif", color: card.color }}>{card.value}</p>
              <p className="text-xs text-[oklch(0.45_0.02_240)]">{card.sub}</p>
            </div>
          ))}
        </div>

        {/* Alert summary + Quick links — single 4-column row */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          {[
            { label: "Not Started",    desc: "Monthly clients not yet begun",   count: missingDataCount,  href: null },
            { label: "P&L for Review", desc: "Awaiting Controller review",      count: plReadyFiles ?? 0, href: "/documents" },
          ].map((alert) => (
            <div
              key={alert.label}
              className="apx-card rounded-xl p-4 flex items-center gap-3 cursor-pointer"
              onClick={() => {
                if (alert.href) navigate(alert.href);
                else toast.info(`${alert.label}`, { description: alert.desc });
              }}
            >
              <div
                className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-lg font-bold"
                style={{ background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)" }}
              >
                {alert.count === null ? "…" : alert.count}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[oklch(0.18_0.04_240)] truncate">{alert.label}</p>
                <p className="text-sm text-[oklch(0.35_0.03_240)] leading-snug">{alert.desc}</p>
              </div>
            </div>
          ))}
          {/* Tier Reconciliation card — replaces Overdue, same card height */}
          <div className="apx-card rounded-xl p-4 col-span-2 xl:col-span-1">
            <div className="flex items-center justify-between gap-1">
              {([
                { key: "nys",  label: "NYS",   value: tierReconCounts.nys,  color: "oklch(0.45 0.02 240)", numColor: "oklch(0.12 0.02 240)" },
                { key: "t1",   label: "T1",    value: tierReconCounts.t1,   color: "oklch(0.55 0.14 75)",  numColor: "oklch(0.12 0.02 240)" },
                { key: "t2",   label: "T2",    value: tierReconCounts.t2,   color: "oklch(0.45 0.15 240)", numColor: "oklch(0.12 0.02 240)" },
                { key: "t3",   label: "T3",    value: tierReconCounts.t3,   color: "oklch(0.45 0.18 300)", numColor: "oklch(0.12 0.02 240)" },
                { key: "t3c",  label: "C-P&L", value: tierReconCounts.t3c,  color: "oklch(0.40 0.14 145)", numColor: "oklch(0.45 0.16 145)" },
              ] as const).map((col) => (
                <div key={col.key} className="flex flex-col items-center flex-1 gap-0.5">
                  <span className="text-sm font-bold" style={{ color: col.color }}>{col.label}</span>
                  <span
                    className="text-base font-bold tabular-nums"
                    style={{ color: col.numColor }}
                  >
                    {col.value === null ? "…" : col.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {/* Xero quick link — 4th column */}
          {QUICK_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="apx-card rounded-xl p-4 flex items-start gap-3 no-underline group"
            >
              <span className="text-2xl">{link.icon}</span>
              <div>
                <p className="text-sm font-semibold text-[oklch(0.18_0.04_240)] group-hover:text-[oklch(0.08_0.01_240)] transition-colors">
                  {link.label}
                </p>
                <p className="text-sm text-[oklch(0.35_0.03_240)] leading-snug mt-0.5">{link.desc}</p>
              </div>
            </a>
          ))}
        </div>

        {/* ── Two-column main layout (matches mockup) ──────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-6 items-start">
          {/* LEFT COLUMN: Client Portfolio + Sent Messages */}
          <div className="space-y-6">
          {/* Client Portfolio — live from DB with search/filter */}
          <div className="apx-card rounded-xl p-5">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <h3
                  className="text-base font-semibold text-[oklch(0.08_0.01_240)]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Client Portfolio
                </h3>
                {/* Monitoring period badge: shows previous month from 5th onward, month-before-last before the 5th */}
                {(() => {
                  const today = new Date();
                  const day = today.getDate();
                  const offset = day < 5 ? 2 : 1;
                  const periodStart = new Date(today.getFullYear(), today.getMonth() - offset, 1);
                  const periodEnd = new Date(today.getFullYear(), today.getMonth() - offset + 1, 0);
                  const fmt = (d: Date) =>
                    d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
                  return (
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-md border whitespace-nowrap"
                      style={{
                        color: "oklch(0.45 0.18 50)",
                        borderColor: "oklch(0.72 0.14 75 / 45%)",
                        background: "oklch(0.97 0.02 75 / 70%)",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {fmt(periodStart)} – {fmt(periodEnd)}
                    </span>
                  );
                })()}
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <span className="text-sm text-[oklch(0.35_0.03_240)]">
                  {clientsLoading ? "Loading…" : `${filteredClients.length} of ${allClients?.length ?? 0} clients`}
                </span>
                {clientDataUpdatedAt && (
                  <span
                    className="text-[0.68rem] font-medium"
                    style={{ color: "oklch(0.50 0.06 145)", fontFamily: "'DM Sans', sans-serif" }}
                  >
                    ✓ Last updated: {clientDataUpdatedAt.toLocaleString("en-US", {
                      month: "short", day: "numeric", year: "numeric",
                      hour: "numeric", minute: "2-digit", hour12: true
                    })}
                  </span>
                )}
              </div>
            </div>
            {/* Tier color legend */}
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="text-xs text-[oklch(0.45_0.03_240)] font-medium">Status key:</span>
              {[
                { label: "T1 — Bank Feed",    color: "oklch(0.55 0.16 85)" },
                { label: "T2 — Coding",       color: "oklch(0.45 0.18 240)" },
                { label: "T3 — Anomaly/T&B",  color: "oklch(0.45 0.20 300)" },
                { label: "C-P&L — P&L (Done)",   color: "oklch(0.40 0.15 145)" },
              ].map(({ label, color }) => (
                <span key={label} className="flex items-center gap-1.5 text-xs font-medium">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                  <span style={{ color: "oklch(0.30 0.03 240)" }}>{label}</span>
                </span>
              ))}
            </div>

            {/* Search + filter bar */}
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
                  style={{ color: "oklch(0.22 0.04 240)" }}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search by client name or B/Feed / Code bookkeeper…"
                  className="w-full pl-8 pr-3 py-1.5 rounded-lg text-sm outline-none"
                  style={{
                    background: "oklch(0.93 0.008 240)",
                    border: "2px solid oklch(0.25 0.04 240)",
                    color: "oklch(0.12 0.04 240)",
                  }}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 py-1.5 rounded-lg text-sm outline-none shrink-0"
                style={{
                  background: "oklch(0.93 0.008 240)",
                  border: "2px solid oklch(0.25 0.04 240)",
                  color: "oklch(0.28 0.02 85)",
                }}
              >
                <option value="all">All Statuses</option>
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="complete">Complete</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>

            {/* Client rows */}
            <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
              {clientsLoading ? (
                <div className="flex items-center justify-center py-10">
                  <span className="text-sm text-[oklch(0.30_0.03_240)] animate-pulse">Loading clients from database…</span>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                  <span className="text-sm text-[oklch(0.30_0.03_240)]">
                    {clientSearch || statusFilter !== "all" ? "No clients match your search." : "No clients in database yet."}
                  </span>
                </div>
              ) : (
                filteredClients.map((client) => {
                  const derivedStatus = clientStatusFromTiers[client.id] ?? "not_started";
                  const statusInfo = STATUS_DISPLAY[derivedStatus] ?? { label: derivedStatus, color: "oklch(0.22 0.04 240)" };
                  const isAnnualClient = client.isAnnual;
                  const isWorkCycleOverrideActive = client.workCycleOverrideActive;
                  const _currentMonth = new Date().getMonth();
                  const _biAnnualActiveMonths = [0, 6, 11];
                  const isBiAnnualActive = isAnnualClient && (_biAnnualActiveMonths.includes(_currentMonth) || isWorkCycleOverrideActive);
                  const isBiAnnualInactive = isAnnualClient && !_biAnnualActiveMonths.includes(_currentMonth) && !isWorkCycleOverrideActive;
                  return (
                    <div key={client.id} className="space-y-0">
                    <div
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group"
                      style={{ background: isBiAnnualInactive ? "oklch(0.88 0.01 270 / 60%)" : isBiAnnualActive ? "oklch(0.90 0.02 270 / 40%)" : "oklch(0.93 0.008 240)", borderRadius: expandedDrive === client.clientName ? "0.5rem 0.5rem 0 0" : undefined, opacity: isBiAnnualInactive ? 0.75 : 1 }}
                    >
                      <div
                        className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                        style={{ background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)" }}
                      >
                        {client.clientName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-[oklch(0.18_0.04_240)] truncate">
                            {client.clientName}
                          </p>
                          {(client as any).accountingPlatform === "quickbooks" && (
                            <span title="QuickBooks" className="shrink-0 text-[9px] font-bold px-1 py-0.5 rounded" style={{ background: "oklch(0.55 0.18 145 / 15%)", color: "oklch(0.35 0.18 145)", border: "1px solid oklch(0.55 0.18 145 / 30%)", lineHeight: 1 }}>QB</span>
                          )}
                          {(client as any).accountingPlatform === "other" && (
                            <span title="Other platform" className="shrink-0 text-[9px] font-bold px-1 py-0.5 rounded" style={{ background: "oklch(0.55 0.05 240 / 15%)", color: "oklch(0.35 0.05 240)", border: "1px solid oklch(0.55 0.05 240 / 30%)", lineHeight: 1 }}>OTH</span>
                          )}
                          {isAnnualClient && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full shrink-0" style={{ background: isBiAnnualActive ? "oklch(0.35 0.14 145 / 15%)" : "oklch(0.35 0.08 270 / 15%)", color: isBiAnnualActive ? "oklch(0.30 0.14 145)" : "oklch(0.30 0.08 270)", border: isBiAnnualActive ? "1px solid oklch(0.35 0.14 145 / 30%)" : "1px solid oklch(0.35 0.08 270 / 30%)", fontSize: "10px", fontWeight: 600 }}>{isWorkCycleOverrideActive ? "Work Cycle · Active" : isBiAnnualActive ? "Scheduled · Active" : "Scheduled · Inactive"}</span>
                          )}
                          {client.notes && (
                            <span
                              title={client.notes}
                              className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium cursor-default"
                              style={{ background: "oklch(0.72 0.14 75 / 15%)", color: "oklch(0.45 0.12 75)" }}
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              <span className="max-w-[120px] truncate">{client.notes}</span>
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[oklch(0.35_0.03_240)] truncate">
                          B/Feed: {client.bookkeeperBankFeed} · Code: {client.bookkeeperCoding}
                        </p>
                      </div>
                      {/* Client Hub deep-link removed */}
                      {isAnnualClient && !_biAnnualActiveMonths.includes(_currentMonth) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setWorkCycleOverride.mutate({ clientName: client.clientName, active: !isWorkCycleOverrideActive, callerEmail: user?.email ?? "" });
                          }}
                          className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold transition-all duration-150"
                          style={{
                            background: isWorkCycleOverrideActive ? "oklch(0.35 0.08 270 / 12%)" : "oklch(0.35 0.14 145 / 12%)",
                            border: `2px solid ${isWorkCycleOverrideActive ? "oklch(0.35 0.08 270)" : "oklch(0.35 0.14 145)"}`,
                            color: isWorkCycleOverrideActive ? "oklch(0.30 0.08 270)" : "oklch(0.30 0.14 145)",
                          }}
                          title={isWorkCycleOverrideActive ? "Lock this client until its next scheduled cycle" : "Unlock this client for bookkeeping work now"}
                        >
                          {isWorkCycleOverrideActive ? "Return to Schedule" : "Activate Work"}
                        </button>
                      )}
                      {/* Drive Docs button */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setExpandedDrive(expandedDrive === client.clientName ? null : client.clientName); }}
                        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold transition-all duration-150"
                        style={{
                          background: expandedDrive === client.clientName ? "oklch(0.92 0.005 240 / 20%)" : "oklch(0.92 0.005 240 / 10%)",
                          border: `1px solid oklch(0.78 0.01 240 / ${expandedDrive === client.clientName ? "80" : "40"}%)`,
                          color: "oklch(0.08 0.01 240)",
                        }}
                        title={`${expandedDrive === client.clientName ? "Hide" : "View"} Drive documents`}
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 15l4.5-8h9L21 15M3 15h18M3 15l2 4h14l2-4" />
                        </svg>
                        Docs
                      </button>
                      {/* Per-row Notify Client button */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setNotifyClientDefaults({ clientName: client.clientName }); setShowNotifyClient(true); }}
                        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold transition-all duration-150"
                        style={{
                          background: "oklch(0.92 0.005 240 / 12%)",
                          border: "2px solid oklch(0.25 0.04 240)",
                          color: "oklch(0.08 0.01 240)",
                        }}
                        title={`Notify ${client.clientName}`}
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Notify
                      </button>
                      {/* Contact Client (SMS/Email) */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setContactModal({ clientName: client.clientName, clientEmail: client.clientEmail ?? null, clientPhone: client.clientPhone ?? null }); }}
                        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold transition-all duration-150"
                        style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}
                        title={`Contact ${client.clientName}`}
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3v-3z" />
                        </svg>
                        Contact
                      </button>
                      {/* Inline contact edit pencil */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingContact === client.clientName) {
                            setEditingContact(null);
                          } else {
                            setEditingContact(client.clientName);
                            setEditContactEmail(client.clientEmail ?? "");
                            setEditContactPhone(client.clientPhone ?? "");
                            setEditContactNotes(client.notes ?? "");
                          }
                        }}
                        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold transition-all duration-150"
                        style={{
                          background: editingContact === client.clientName ? "oklch(0.72 0.14 75 / 15%)" : "oklch(0.92 0.005 240 / 12%)",
                          border: `2px solid ${editingContact === client.clientName ? "oklch(0.72 0.14 75)" : "oklch(0.25 0.04 240)"}`,
                          color: editingContact === client.clientName ? "oklch(0.45 0.12 75)" : "oklch(0.08 0.01 240)",
                        }}
                        title={editingContact === client.clientName ? "Close edit" : "Edit contact info"}
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        Edit
                      </button>
                      <span
                        className="text-sm px-2 py-0.5 rounded-full shrink-0 font-medium"
                        style={{
                          background: `${statusInfo.color}18`,
                          color: statusInfo.color,
                        }}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                    {/* Inline contact edit strip */}
                    {editingContact === client.clientName && (
                      <div
                        className="px-3 py-3 rounded-b-lg"
                        style={{ background: "oklch(0.96 0.012 75 / 40%)", border: "2px solid oklch(0.72 0.14 75 / 40%)", borderTop: "none" }}
                      >
                        <p className="text-sm font-semibold mb-2" style={{ color: "oklch(0.35 0.08 75)" }}>Edit Contact Info — {client.clientName}</p>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <label className="text-sm w-14 shrink-0" style={{ color: "oklch(0.28 0.04 240)" }}>Email</label>
                            <input
                              type="email"
                              value={editContactEmail}
                              onChange={(e) => setEditContactEmail(e.target.value)}
                              placeholder="client@example.com"
                              className="flex-1 px-2 py-1 rounded-md text-sm outline-none"
                              style={{ background: "oklch(0.97 0.005 240)", border: "1.5px solid oklch(0.72 0.14 75 / 60%)", color: "oklch(0.12 0.04 240)" }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-sm w-14 shrink-0" style={{ color: "oklch(0.28 0.04 240)" }}>Phone</label>
                            <input
                              type="tel"
                              value={editContactPhone}
                              onChange={(e) => setEditContactPhone(e.target.value)}
                              placeholder="+1 (310) 555-0100"
                              className="flex-1 px-2 py-1 rounded-md text-sm outline-none"
                              style={{ background: "oklch(0.97 0.005 240)", border: "1.5px solid oklch(0.72 0.14 75 / 60%)", color: "oklch(0.12 0.04 240)" }}
                            />
                          </div>
                          <div className="flex items-start gap-2">
                            <label className="text-sm w-14 shrink-0 pt-1" style={{ color: "oklch(0.28 0.04 240)" }}>Notes</label>
                            <textarea
                              value={editContactNotes}
                              onChange={(e) => setEditContactNotes(e.target.value)}
                              placeholder="Internal notes about this client…"
                              rows={3}
                              className="flex-1 px-2 py-1 rounded-md text-sm outline-none resize-y"
                              style={{ background: "oklch(0.97 0.005 240)", border: "1.5px solid oklch(0.72 0.14 75 / 60%)", color: "oklch(0.12 0.04 240)", minHeight: "60px" }}
                            />
                          </div>
                          <div className="flex gap-2 mt-1">
                            <button
                              type="button"
                              disabled={updateContactMutation.isPending}
                              onClick={() => updateContactMutation.mutate({
                                clientName: client.clientName,
                                email: editContactEmail || null,
                                phone: editContactPhone || null,
                                notes: editContactNotes || null,
                              })}
                              className="px-3 py-1 rounded-md text-sm font-semibold transition-all"
                              style={{ background: "oklch(0.72 0.14 75)", color: "oklch(0.08 0.01 240)", opacity: updateContactMutation.isPending ? 0.6 : 1 }}
                            >
                              {updateContactMutation.isPending ? "Saving…" : "Save"}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingContact(null)}
                              className="px-3 py-1 rounded-md text-sm font-medium transition-all"
                              style={{ background: "oklch(0.92 0.005 240 / 40%)", color: "oklch(0.28 0.04 240)", border: "1px solid oklch(0.25 0.04 240 / 30%)" }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Expandable Drive documents panel */}
                    {expandedDrive === client.clientName && (
                      <div
                        className="rounded-b-lg overflow-hidden"
                        style={{ border: "2px solid oklch(0.25 0.04 240)", background: "oklch(0.94 0.018 220)", borderTop: "none" }}
                      >
                        <div className="px-3 py-2 flex items-center gap-2" style={{ borderBottom: "1px solid oklch(0.92 0.005 240 / 15%)", background: "oklch(0.95 0.006 240)" }}>
                          <svg className="w-3.5 h-3.5" style={{ color: "oklch(0.08 0.01 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 15l4.5-8h9L21 15M3 15h18M3 15l2 4h14l2-4" />
                          </svg>
                          <span className="text-sm font-semibold" style={{ color: "oklch(0.08 0.01 240)" }}>Drive Documents — {client.clientName}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setExpandedDrive(null); }}
                            className="ml-auto text-[oklch(0.30_0.03_240)] hover:text-[oklch(0.08_0.01_240)] transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="p-3">
                          <ClientDocuments clientName={client.clientName} />
                        </div>
                      </div>
                    )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

           </div>{/* end left column */}
          {/* RIGHT COLUMN: Team Workload + Assign Tax Task + Inbox panel — sticky */}
          <div className="xl:sticky xl:top-6 space-y-5">
            {/* Schedule & Task Summary — identical to Bookkeeping Manager view */}
            <div className="apx-card rounded-xl p-5">
              <h3 className="text-base font-semibold text-[oklch(0.08_0.01_240)] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Schedule & Task Summary
              </h3>
              <p className="text-sm text-[oklch(0.30_0.03_240)] mb-3">Clients-Bkkpr Highest Tier</p>

              {/* ── T3 vs C-P&L Progress Bar ── */}
              {(() => {
                const totalT3 = workload.reduce((s, bk) => s + bk.t3, 0);
                const totalT3C = workload.reduce((s, bk) => s + bk.t3c, 0);
                const grandTotal = totalT3 + totalT3C;
                const t3Pct = grandTotal > 0 ? Math.round((totalT3 / grandTotal) * 100) : 0;
                const t3cPct = grandTotal > 0 ? 100 - t3Pct : 0;
                // Per-bookkeeper breakdown for tooltips (exclude zero-count entries)
                const t3Breakdown = workload.filter(bk => bk.t3 > 0).map(bk => `${bk.name}: ${bk.t3}`);
                const t3cBreakdown = workload.filter(bk => bk.t3c > 0).map(bk => `${bk.name}: ${bk.t3c}`);
                return (
                  <div className="mb-4 px-1">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold" style={{ color: CTRL_TIER_META.T3.color }}>T3 In-Progress: {totalT3}</span>
                      <span className="text-xs font-semibold" style={{ color: CTRL_TIER_META["T3-C"].color }}>C-P&L Completed: {totalT3C}</span>
                    </div>
                    <div className="relative h-4 rounded-full overflow-hidden flex" style={{ background: "oklch(0.91 0.01 240)" }}>
                      {totalT3 > 0 && (
                        <div className="group relative h-full" style={{ width: `${t3Pct}%`, minWidth: 28 }}>
                          <div
                            className="h-full flex items-center justify-center text-[10px] font-bold text-white transition-all duration-700 cursor-pointer w-full"
                            style={{ background: CTRL_TIER_META.T3.color }}
                          >
                            {t3Pct > 12 ? `${t3Pct}%` : ""}
                          </div>
                          {/* T3 tooltip */}
                          <div className="absolute bottom-full left-0 mb-2 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                            <div className="rounded-lg shadow-xl px-3 py-2.5 text-xs whitespace-nowrap" style={{ background: "oklch(0.12 0.04 240)", border: `1px solid ${CTRL_TIER_META.T3.color}40`, minWidth: 160 }}>
                              <div className="font-bold mb-1.5" style={{ color: CTRL_TIER_META.T3.color }}>T3 In-Progress — {totalT3} clients</div>
                              {t3Breakdown.length > 0
                                ? t3Breakdown.map((line, i) => (
                                    <div key={i} className="flex items-center justify-between gap-4 py-0.5">
                                      <span style={{ color: "oklch(0.80 0.01 240)" }}>{line.split(":")[0]}</span>
                                      <span className="font-semibold" style={{ color: CTRL_TIER_META.T3.color }}>{line.split(":")[1]}</span>
                                    </div>
                                  ))
                                : <div style={{ color: "oklch(0.60 0.01 240)" }}>No assignments</div>
                              }
                              <div className="mt-1.5 pt-1.5" style={{ borderTop: `1px solid ${CTRL_TIER_META.T3.color}30`, color: "oklch(0.55 0.02 240)" }}>
                                {t3Pct}% of T3/C-P&L total
                              </div>
                            </div>
                            <div className="w-2 h-2 rotate-45 ml-3" style={{ background: "oklch(0.12 0.04 240)", marginTop: -5 }} />
                          </div>
                        </div>
                      )}
                      {totalT3C > 0 && (
                        <div className="group relative h-full" style={{ width: `${t3cPct}%`, minWidth: 28 }}>
                          <div
                            className="h-full flex items-center justify-center text-[10px] font-bold text-white transition-all duration-700 cursor-pointer w-full"
                            style={{ background: CTRL_TIER_META["T3-C"].color }}
                          >
                            {t3cPct > 12 ? `${t3cPct}%` : ""}
                          </div>
                          {/* C-P&L tooltip */}
                          <div className="absolute bottom-full right-0 mb-2 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                            <div className="rounded-lg shadow-xl px-3 py-2.5 text-xs whitespace-nowrap" style={{ background: "oklch(0.12 0.04 240)", border: `1px solid ${CTRL_TIER_META["T3-C"].color}40`, minWidth: 160 }}>
                              <div className="font-bold mb-1.5" style={{ color: CTRL_TIER_META["T3-C"].color }}>C-P&L Completed — {totalT3C} clients</div>
                              {t3cBreakdown.length > 0
                                ? t3cBreakdown.map((line, i) => (
                                    <div key={i} className="flex items-center justify-between gap-4 py-0.5">
                                      <span style={{ color: "oklch(0.80 0.01 240)" }}>{line.split(":")[0]}</span>
                                      <span className="font-semibold" style={{ color: CTRL_TIER_META["T3-C"].color }}>{line.split(":")[1]}</span>
                                    </div>
                                  ))
                                : <div style={{ color: "oklch(0.60 0.01 240)" }}>No completions yet</div>
                              }
                              <div className="mt-1.5 pt-1.5" style={{ borderTop: `1px solid ${CTRL_TIER_META["T3-C"].color}30`, color: "oklch(0.55 0.02 240)" }}>
                                {t3cPct}% of T3/C-P&L total
                              </div>
                            </div>
                            <div className="w-2 h-2 rotate-45 ml-auto mr-3" style={{ background: "oklch(0.12 0.04 240)", marginTop: -5 }} />
                          </div>
                        </div>
                      )}
                      {grandTotal === 0 && (
                        <div className="w-full h-full flex items-center justify-center text-xs text-[oklch(0.50_0.02_240)]">No data</div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-[oklch(0.45_0.03_240)]">of {grandTotal} total T3/C-P&L clients</span>
                      {grandTotal > 0 && <span className="text-[10px] font-semibold" style={{ color: CTRL_TIER_META["T3-C"].color }}>{t3cPct}% complete</span>}
                    </div>
                  </div>
                );
              })()}

              {/* Tier legend */}
              <div className="flex gap-3 mb-3">
                {Object.entries(CTRL_TIER_META).map(([k, v]) => (
                  <span key={k} className="text-sm font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ background: `${v.color}15`, color: v.color }}>
                    {k}={v.code}
                  </span>
                ))}
              </div>
              {/* Summary cards grid — all bookkeepers incl. Jany (excl. Gus Controller) */}
               <div className="grid grid-cols-2 gap-2 mb-3">
                 {workload.filter(bk => bk.name !== "Gus G.").map((bk) => {
                  const isExpanded = expandedBkCard === bk.email;
                  return (
                    <div key={bk.email} className={`rounded-lg p-3 cursor-pointer transition-all duration-200 ${isExpanded ? "col-span-2" : ""}`}
                      style={{ background: isExpanded ? "oklch(0.91 0.012 240)" : "oklch(0.93 0.008 240)", border: `1px solid ${isExpanded ? bk.color : bk.color + "20"}`, boxShadow: isExpanded ? `0 0 0 2px ${bk.color}30` : undefined }}
                      onClick={() => setExpandedBkCard(isExpanded ? null : bk.email)}>
                      {/* Card header */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                          style={{ background: `${bk.color}20`, color: bk.color }}>
                          {bk.initials}
                        </div>
                        <span className="text-sm font-semibold text-[oklch(0.18_0.04_240)] truncate">{bk.name}</span>
                        <div className="ml-auto flex items-center gap-1">
                          {(ctrlTaskNotifCounts[bk.email] ?? 0) > 0 && (
                            <span
                              title={`${ctrlTaskNotifCounts[bk.email]} unacknowledged task request(s)`}
                              className="text-sm font-bold px-1.5 py-0.5 rounded-full"
                              style={{ background: "oklch(0.92 0.005 240 / 20%)", color: "oklch(0.08 0.01 240)", border: "2px solid oklch(0.25 0.04 240)" }}>
                              {ctrlTaskNotifCounts[bk.email]}✉
                            </span>
                          )}
                          {bk.overdue > 0 && (
                            <span className="text-sm font-bold px-1 py-0.5 rounded"
                              style={{ background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)" }}>
                              {bk.overdue}⚠
                            </span>
                          )}
                          {/* Expand/collapse chevron */}
                          <svg className={`w-3.5 h-3.5 ml-1 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                            style={{ color: bk.color }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      {/* Tier bars */}
                      {(["T1", "T2", "T3", "T3-C"] as const).map(tier => {
                        const count = tier === "T1" ? bk.t1 : tier === "T2" ? bk.t2 : tier === "T3" ? bk.t3 : bk.t3c;
                        const meta = CTRL_TIER_META[tier];
                        const maxCount = Math.max(...workload.map(w => tier === "T1" ? w.t1 : tier === "T2" ? w.t2 : tier === "T3" ? w.t3 : w.t3c), 1);
                        const pctBar = Math.round((count / maxCount) * 100);
                        return (
                          <div key={tier} className="flex items-center gap-1.5 mb-1">
                            <span className="text-sm font-bold w-8 shrink-0 whitespace-nowrap" style={{ color: meta.color }}>{tier}</span>
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "oklch(0.94 0.018 220)" }}>
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pctBar}%`, background: meta.color }} />
                            </div>
                            <span className="text-sm font-mono text-[oklch(0.08_0.01_240)] w-4 text-right">{count}</span>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between mt-1.5 pt-1.5" style={{ borderTop: "1px solid oklch(1 0 0 / 6%)" }}>
                        <span className="text-sm text-[oklch(0.30_0.03_240)]">Total: {bk.t1 + bk.t2 + bk.t3 + bk.t3c}</span>
                        {bk.complete > 0 && <span className="text-sm font-semibold" style={{ color: "oklch(0.08 0.01 240)" }}>{bk.complete} done</span>}
                      </div>
                      {/* Expanded client list */}
                      {isExpanded && (
                        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${bk.color}30` }}
                          onClick={e => e.stopPropagation()}>
                          <p className="text-xs font-semibold mb-2" style={{ color: bk.color }}>
                            {bk.name}’s Assigned Clients ({bk.clients.length})
                          </p>
                          {bk.clients.length === 0 ? (
                            <p className="text-xs text-[oklch(0.45_0.02_240)]">No clients assigned at highest tier.</p>
                          ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                              {bk.clients.map((c, ci) => {
                                const tierMeta = CTRL_TIER_META[c.tier as keyof typeof CTRL_TIER_META];
                                const statusColor = c.status === "complete" ? "oklch(0.45 0.15 145)" : c.status === "overdue" ? "oklch(0.45 0.18 25)" : "oklch(0.45 0.08 240)";
                                return (
                                  <div key={ci} className="flex items-center gap-1.5 px-2 py-1 rounded"
                                    style={{ background: "oklch(0.95 0.005 240)" }}>
                                    <span className="text-[10px] font-bold px-1 rounded shrink-0"
                                      style={{ background: `${tierMeta?.color ?? "#888"}20`, color: tierMeta?.color ?? "#888" }}>
                                      {c.tier}
                                    </span>
                                    <span className="text-xs text-[oklch(0.18_0.04_240)] truncate flex-1" title={c.clientName ?? ""}>{c.clientName}</span>
                                    <span className="text-[10px] font-semibold shrink-0" style={{ color: statusColor }}>
                                      {c.status === "complete" ? "✓" : c.status === "overdue" ? "!" : ""}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Manager row — Gus (Controller) only */}
               <div className="space-y-1.5">
                 {workload.filter(bk => bk.name === "Gus G.").map(bk => (
                  <div key={bk.email} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: "oklch(0.93 0.008 240)", border: `1px solid oklch(0.35 0.08 270 / 15%)` }}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                      style={{ background: "oklch(0.35 0.08 270 / 15%)", color: "oklch(0.25 0.08 270)" }}>
                      {bk.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[oklch(0.18_0.04_240)]">
                          {bk.name}
                          <span className="ml-1 text-sm font-normal" style={{ color: "oklch(0.35 0.08 270)" }}>
                            ({bk.name === "Jany L." ? "Bkkpr Mgr" : "Controller"})
                          </span>
                        </span>
                        <span className="text-sm font-mono text-[oklch(0.08_0.01_240)]">{bk.t1 + bk.t2 + bk.t3 + bk.t3c} total</span>
                      </div>
                      <div className="flex gap-2 mt-0.5">
                        {bk.t1 > 0 && <span className="text-sm" style={{ color: CTRL_TIER_META.T1.color }}>T1:{bk.t1}</span>}
                        {bk.t2 > 0 && <span className="text-sm" style={{ color: CTRL_TIER_META.T2.color }}>T2:{bk.t2}</span>}
                        {bk.t3 > 0 && <span className="text-sm" style={{ color: CTRL_TIER_META.T3.color }}>T3:{bk.t3}</span>}
                        {bk.t3c > 0 && <span className="text-sm" style={{ color: CTRL_TIER_META["T3-C"].color }}>C-P&L:{bk.t3c}</span>}
                        {bk.overdue > 0 && <span className="text-sm font-semibold" style={{ color: "oklch(0.08 0.01 240)" }}>{bk.overdue} overdue</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>



          </div>{/* end right column */}

        </div>{/* end two-column grid */}

        {/* ── BOTTOM: SMS (left) + All Communications (right) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-5 mt-5 items-stretch">
          <div className="apx-card rounded-xl overflow-hidden flex flex-col">
            <SmsCombinedPanel />
          </div>
          <div className="apx-card rounded-xl overflow-hidden p-4" style={{ minHeight: 420 }}>
            <AllCommunicationsFeed
              email={user?.email ?? ""}
              role={user?.role ?? "controller"}
              userName={user?.name ?? "Controller"}
              userRole={user?.role ?? "controller"}
            />
          </div>
        </div>
      </div>

      {showNotify && (
        <SendNotificationModal
          onClose={() => setShowNotify(false)}
          defaultRecipient="email:lopez.jany@gmail.com"
          defaultType="task"
        />
      )}

      {showNotifyClient && (
        <NotifyClientModal
          onClose={() => setShowNotifyClient(false)}
          defaultClientName={notifyClientDefaults.clientName ?? ""}
        />
      )}

      {/* Contact Client Modal */}
      {contactModal && (
        <ContactClientModal
          open={!!contactModal}
          onClose={() => setContactModal(null)}
          clientName={contactModal.clientName}
          clientEmail={contactModal.clientEmail}
          clientPhone={contactModal.clientPhone}
          senderName={user?.name ?? "APX Team"}
          senderEmail={user?.email ?? ""}
          userRole={user?.role ?? "controller"}
        />
      )}
          {/* Monthly Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "oklch(0 0 0 / 50%)" }}>
          <div className="rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl" style={{ background: "oklch(0.97 0.005 240)", border: "2px solid oklch(0.55 0.18 25 / 40%)" }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "oklch(0.55 0.18 25 / 15%)" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="oklch(0.45 0.18 25)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-base" style={{ color: "oklch(0.12 0.04 240)", fontFamily: "'Playfair Display', serif" }}>Reset Monthly Matrix</h3>
                <p className="text-xs" style={{ color: "oklch(0.40 0.03 240)" }}>SOP — 5th of the Month</p>
              </div>
            </div>
            <p className="text-sm mb-5" style={{ color: "oklch(0.25 0.03 240)" }}>
              This will reset <strong>all bookkeeping progress</strong> back to <strong>Not Started</strong> for every active client. Due dates, notes, and secondary assignees will be cleared.
            </p>
            <p className="text-xs mb-5 px-3 py-2 rounded-lg" style={{ background: "oklch(0.55 0.18 25 / 8%)", color: "oklch(0.40 0.18 25)", border: "1px solid oklch(0.55 0.18 25 / 25%)" }}>
              This action cannot be undone. Only proceed if you are starting a new billing month.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{ background: "oklch(0.92 0.005 240 / 15%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.25 0.04 240)" }}>
                Cancel
              </button>
              <button
                onClick={() => resetMonthlyMutation.mutate({ callerName: user?.name ?? "Controller", callerEmail: user?.email ?? "" })}
                disabled={resetMonthlyMutation.isPending}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-bold transition-all"
                style={{ background: "oklch(0.55 0.18 25)", border: "2px solid oklch(0.50 0.18 25)", color: "oklch(0.97 0.005 240)", opacity: resetMonthlyMutation.isPending ? 0.7 : 1 }}>
                {resetMonthlyMutation.isPending ? "Resetting…" : "Yes, Reset Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
// deployment trigger Thu Apr 30 01:40:11 EDT 2026
